import { type ReactNode } from 'react';
import { cn } from '@/utils/cn';

export interface TabItem {
  name: string;
  label: string;
  icon: (active: boolean) => ReactNode;
  center?: boolean;
}

export interface TabbarProps {
  items: TabItem[];
  active: string;
  onChange?: (name: string) => void;
  className?: string;
  variant?: 'light' | 'dark';
}

const Tabbar = ({ items, active, onChange, className, variant = 'light' }: TabbarProps) => {
  const handleClick = (name: string) => {
    onChange?.(name);
  };

  const isDark = variant === 'dark';

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-1/2 -translate-x-1/2 z-50',
        'flex items-center justify-around',
        'h-14 w-full max-w-[460px]',
        isDark
          ? 'border-t border-white/18 bg-[#14113d]/68 shadow-[0_-12px_34px_rgba(12,8,42,0.36),inset_0_1px_0_rgba(255,255,255,0.2)] backdrop-blur-[28px] backdrop-saturate-150 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-white/24 after:absolute after:inset-x-4 after:top-1 after:h-5 after:rounded-full after:bg-white/8 after:blur-xl'
          : 'border-t border-white/50 bg-white/68 shadow-[0_-12px_34px_rgba(15,23,42,0.18),inset_0_1px_0_rgba(255,255,255,0.72)] backdrop-blur-[28px] backdrop-saturate-150 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-white/75 after:absolute after:inset-x-4 after:top-1 after:h-5 after:rounded-full after:bg-white/35 after:blur-xl',
        className,
      )}
    >
      {items.map((item) => {
        const isActive = active === item.name;
        return (
          <button
            key={item.name}
            onClick={() => handleClick(item.name)}
            className={cn(
              'relative flex h-full flex-1 flex-col items-center justify-center',
              'transition-all active:scale-95 active:opacity-80',
              isDark && 'text-white/68 after:absolute after:right-0 after:top-3 after:h-8 after:w-px after:bg-white/10 last:after:hidden',
              item.center && 'relative -top-2',
            )}
          >
            {isDark && isActive && <div className="absolute top-1 h-8 w-11 rounded-full bg-fuchsia-400/28 blur-md" />}
            <div className={cn('relative mb-1', item.center && 'scale-125')}>{item.icon(isActive)}</div>
            {item.label && (
              <span
                className={cn(
                  'relative text-xs font-medium transition-colors',
                  isDark ? (isActive ? 'text-white drop-shadow-[0_0_8px_rgba(217,70,239,0.9)]' : 'text-white/68') : (isActive ? 'text-primary' : 'text-text-secondary')
                )}
              >
                {item.label}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
};

export default Tabbar;
